/**
 * 땅주인 마이페이지 (/owner/mypage)
 * 내 등록 내역 조회
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePageTitle } from "../../hooks/usePageTitle";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { ownerApiFetch } from "../../utils/ownerApi";

const STATUS_LABEL: Record<string, { label: string; color: string; bg: string }> = {
  pending:  { label: "대기중",    color: "#D4A853", bg: "rgba(212,168,83,0.12)" },
  matched:  { label: "중개사 연결됨", color: "#3B82F6", bg: "rgba(59,130,246,0.12)" },
  accepted: { label: "수락됨",    color: "#10B981", bg: "rgba(16,185,129,0.12)" },
  closed:   { label: "완료",      color: "#6B7280", bg: "rgba(107,114,128,0.12)" },
};

export function OwnerMyPage() {
  usePageTitle("내 등록 내역");
  const navigate = useNavigate();
  const m = useIsMobile();

  const [listings, setListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const ownerInfo = (() => {
    try {
      const s = localStorage.getItem("owner_info");
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  })();

  useEffect(() => {
    if (!localStorage.getItem("owner_token")) {
      navigate("/owner/login", { replace: true });
      return;
    }
    ownerApiFetch("/api/owner/listings/my")
      .then((d: any) => setListings(d.listings || []))
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("owner_token");
    localStorage.removeItem("owner_info");
    navigate("/", { replace: true });
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" as const, background: "var(--bg-primary)" }}>
      <AppHeader />
      <div style={{ flex: 1, maxWidth: 900, margin: "0 auto", width: "100%", padding: m ? "20px 16px 40px" : "40px 32px 60px" }}>

        <div style={{ paddingBottom: 20, borderBottom: "1px solid var(--border-light)", marginBottom: 28, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: "var(--text-primary)", margin: "0 0 4px" }}>
              {"🌱"} 내 등록 내역
            </h1>
            <p style={{ fontSize: 14, color: "var(--text-muted)", margin: 0 }}>
              {ownerInfo?.name}님의 토지 등록 현황입니다
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => navigate("/owner/register")}
              style={{ padding: "9px 18px", background: "linear-gradient(135deg,#B8972E,#D4AF37)", color: "#0F172A", border: "none", borderRadius: "var(--radius-md)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
            >
              {"+"} 땅 추가 등록
            </button>
            <button
              onClick={logout}
              style={{ padding: "9px 18px", background: "var(--bg-card)", color: "var(--text-muted)", border: "1px solid var(--border-medium)", borderRadius: "var(--radius-md)", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
            >
              로그아웃
            </button>
          </div>
        </div>

        {loading ? (
          <p style={{ color: "var(--text-muted)", textAlign: "center" as const, padding: "40px 0" }}>불러오는 중...</p>
        ) : listings.length === 0 ? (
          <div style={{ textAlign: "center" as const, padding: "60px 20px" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{"📋"}</div>
            <p style={{ color: "var(--text-muted)", fontSize: 15, margin: "0 0 20px" }}>등록된 토지가 없습니다</p>
            <button
              onClick={() => navigate("/owner/register")}
              style={{ padding: "12px 28px", background: "linear-gradient(135deg,#B8972E,#D4AF37)", color: "#0F172A", border: "none", borderRadius: "var(--radius-md)", fontWeight: 700, fontSize: 15, cursor: "pointer" }}
            >
              첫 번째 땅 등록하기
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" as const, gap: 14 }}>
            {listings.map((l: any) => {
              const st = STATUS_LABEL[l.status] || STATUS_LABEL.pending;
              return (
                <div key={l.id} style={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border-light)",
                  borderLeft: "3px solid var(--lt-gold)",
                  borderRadius: "var(--radius-xl)",
                  padding: m ? "16px" : "20px 24px",
                  boxShadow: "var(--shadow-sm)",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, flexWrap: "wrap" as const, gap: 8 }}>
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>{l.address || "주소 미입력"}</p>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: st.bg, color: st.color }}>{st.label}</span>
                  </div>
                  <div style={{ display: "flex", gap: 20, flexWrap: "wrap" as const }}>
                    <span style={{ fontSize: 13, color: "var(--text-muted)" }}>지목: <strong style={{ color: "var(--text-secondary)" }}>{l.land_type}</strong></span>
                    <span style={{ fontSize: 13, color: "var(--text-muted)" }}>면적: <strong style={{ color: "var(--text-secondary)" }}>{(l.area_sqm || 0).toLocaleString()}㎡</strong></span>
                    <span style={{ fontSize: 13, color: "var(--text-muted)" }}>희망가: <strong style={{ color: "#FBBF24" }}>{(l.asking_price || 0).toLocaleString()}만원</strong></span>
                  </div>
                  {l.matched_agent_name && (
                    <p style={{ margin: "10px 0 0", fontSize: 13, color: "var(--text-muted)" }}>
                      {"🤝"} 연결 중개사: <strong style={{ color: "var(--text-primary)" }}>{l.matched_agent_name}</strong>
                    </p>
                  )}
                  <p style={{ margin: "8px 0 0", fontSize: 11, color: "var(--text-muted)" }}>
                    등록일: {new Date(l.created_at).toLocaleDateString("ko-KR")}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <AppFooter />
    </div>
  );
}
